import {
  type CreateWorkplaceAgreementRequest,
  PostWorkplaceAgreementRequestSchema,
  PostWorkplaceAgreementResponseSchema,
} from "@clipboard-health/contract-backend-main";
import { post } from "@src/appV2/api/api";
import { environmentConfig } from "@src/appV2/environment/config";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createUseGetWorkplaceAgreementsQueryKey } from "./useGetWorkplaceAgreements";

export function useCreateWorkplaceAgreement(workplaceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateWorkplaceAgreementRequest["data"]["attributes"]) => {
      const response = await post({
        url: `${environmentConfig.REACT_APP_BASE_API_URL}/workplaces/${workplaceId}/agreements`,
        data: {
          data: {
            type: "workplace-agreement",
            attributes: payload,
          },
        },
        requestSchema: PostWorkplaceAgreementRequestSchema,
        responseSchema: PostWorkplaceAgreementResponseSchema,
      });
      return response.data;
    },
    onSuccess: async (response) => {
      const { data } = response;
      // Invalidate by URL prefix so every query variant refreshes (workplace-scoped + scope=user).
      const [agreementsUrl] = createUseGetWorkplaceAgreementsQueryKey(workplaceId);
      await queryClient.invalidateQueries({ queryKey: [agreementsUrl] });
      logEvent(APP_V2_APP_EVENTS.CREATE_WORKPLACE_AGREEMENT_SUCCESS, {
        workplaceAgreementId: data.id,
        ...data.attributes,
      });
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.CREATE_WORKPLACE_AGREEMENT_FAILURE,
    },
  });
}
